import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { SmartPdsUploadModal } from '@client/components/market-pds/SmartPdsUploadModal';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { PERMISSIONS } from '@shared/permissions/permissions';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { FileStack, RefreshCw, Sparkles } from 'lucide-react';
import { type Key, useState } from 'react';
import { match } from 'ts-pattern';
import { OrgPdsFilters } from './OrgPdsFilters';

export const Route = createFileRoute('/console/$orgId/tools/pds-compare/pds-documents')({
    staticData: {
        presentation: 'fullPage',
        breadcrumb: {
            title: 'PDS documents',
        },
        featureFlag: 'enable-pds-comparison',
        permissions: [PERMISSIONS.MARKET_PDS_ACCESS],
        ui: {
            icon: FileStack,
            title: 'My PDS documents',
            subtitle: "Manage your organisation's uploaded PDS documents",
            widthVariant: 'fullWidth',
            menuDescription: 'Manage your PDS documents',
        },
        modules: ['pds-compare'],
    },
    component: OrgPdsList,
});

type QueryParams = RouterInputs['marketPds']['listOrgPds'];
type OrgPdsRow = RouterOutputs['marketPds']['listOrgPds']['rows'][number];
type OrgPdsOrderBy = NonNullable<QueryParams['orderBy']>;

const STATUS_COLORS: Record<string, string> = {
    DRAFT: 'default',
    PROCESSING: 'blue',
    PUBLISHED: 'green',
    ARCHIVED: 'orange',
};

function OrgPdsList() {
    const { orgId } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const { formatDateTime } = useFormatter();
    const [smartUploadOpen, setSmartUploadOpen] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { id: 'desc' },
        where: {},
    });

    const { data, isPending, error } = trpc.marketPds.listOrgPds.useQuery(input, {
        refetchInterval: (query) => {
            const rows = query.state.data?.rows;
            const hasProcessing = rows?.some((r) => r.status === 'PROCESSING');
            return hasProcessing ? 5000 : false;
        },
    });

    const { data: categories } = trpc.marketPds.listCategories.useQuery();

    const { mutate: deleteMutate } = trpc.marketPds.deleteOrgPds.useMutation({
        onError: (err) => errorMessage.show(err),
    });

    const { mutate: reprocessMutate } = trpc.marketPds.reprocessOrgPds.useMutation({
        onError: (err) => errorMessage.show(err),
    });

    const handleReprocess = (id: bigint) => {
        const key = 'reprocess';
        message.loading({ content: 'Queuing reprocessing...', key });
        reprocessMutate(
            { id },
            {
                onSuccess: () => {
                    message.success({ content: 'Reprocessing queued', key });
                    trpcUtils.marketPds.listOrgPds.invalidate();
                },
            },
        );
    };

    const { mutate: bulkReprocessMutate, isPending: isBulkReprocessing } =
        trpc.marketPds.bulkReprocessOrgPds.useMutation({
            onError: (err) => errorMessage.show(err),
        });

    const handleBulkReprocess = () => {
        const ids = selectedRowKeys as bigint[];
        message.loading({ content: `Queuing ${ids.length} for reprocessing...`, key: 'bulk-reprocess' });
        bulkReprocessMutate(
            { ids },
            {
                onSuccess: (result) => {
                    message.success({
                        content: `${result.queued} PDS queued for reprocessing`,
                        key: 'bulk-reprocess',
                    });
                    setSelectedRowKeys([]);
                    trpcUtils.marketPds.listOrgPds.invalidate();
                },
            },
        );
    };

    const columnHelper = createColumnHelper<OrgPdsRow, OrgPdsOrderBy>();

    const columns = [
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            cell: ({ row }) => <span className="font-medium">{row.name}</span>,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('insurerName', {
            id: 'insurerName',
            header: 'Insurer',
            cell: ({ row }) => row.insurerName,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('category', {
            id: 'category',
            header: 'Category',
            cell: ({ row }) => (
                <Tag variant="outlined" color="blue">
                    {row.category}
                </Tag>
            ),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('year', {
            id: 'year',
            header: 'Year',
            cell: ({ row }) => row.year,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: 'Status',
            cell: ({ row }) => (
                <Tag variant="outlined" color={STATUS_COLORS[row.status] ?? 'default'}>
                    {row.status}
                </Tag>
            ),
            className: 'whitespace-nowrap',
        }),
        columnHelper.display({
            id: 'sections',
            header: 'Sections',
            cell: ({ row }) => row._count.Sections,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('processingError', {
            id: 'processingError',
            header: 'Error',
            cell: ({ row }) =>
                row.processingError ? (
                    <span className="max-w-48 truncate text-status-danger" title={row.processingError}>
                        {row.processingError}
                    </span>
                ) : (
                    '-'
                ),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('updatedAt', {
            id: 'updatedAt',
            header: 'Updated',
            cell: ({ row }) => formatDateTime(row.updatedAt),
            className: 'whitespace-nowrap',
        }),
        columnHelper.actions({
            cell: ({ row }) => (
                <Space size="small" className="flex justify-end">
                    {match(row.status)
                        .with('PROCESSING', () => null)
                        .otherwise(() => (
                            <Button.Icon
                                icon={<RefreshCw />}
                                color="cyan"
                                size="small"
                                tooltip="Reprocess"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleReprocess(row.id);
                                }}
                            />
                        ))}
                    <Button.Delete
                        onConfirm={(callback) =>
                            deleteMutate(
                                { id: row.id },
                                {
                                    onSuccess() {
                                        callback();
                                        trpcUtils.marketPds.listOrgPds.invalidate();
                                    },
                                    onError(err) {
                                        errorMessage.show(err);
                                    },
                                },
                            )
                        }
                    />
                </Space>
            ),
            width: 80,
        }),
    ];

    const tableProps = usePrismaTable<OrgPdsRow, QueryParams['where'], OrgPdsOrderBy>({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange(keys) {
                setSelectedRowKeys(keys);
            },
            getCheckboxProps: (record) => ({
                disabled: record.status === 'PROCESSING',
            }),
        },
    });

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary error={error}>
                <TableToolbar
                    filters={
                        <OrgPdsFilters
                            onChange={(where) => setInput((prev) => ({ ...prev, where }))}
                            categories={categories ?? []}
                        />
                    }
                    actions={
                        selectedRowKeys.length > 0 ? (
                            <Space size="small">
                                <span className="whitespace-nowrap text-muted-foreground text-sm">
                                    {selectedRowKeys.length} selected
                                </span>
                                <Button
                                    type="primary"
                                    icon={<RefreshCw />}
                                    onClick={handleBulkReprocess}
                                    loading={isBulkReprocessing}
                                >
                                    Reprocess
                                </Button>
                            </Space>
                        ) : (
                            <Button icon={<Sparkles />} onClick={() => setSmartUploadOpen(true)}>
                                Upload PDS
                            </Button>
                        )
                    }
                />

                <PrismaTable
                    {...tableProps}
                    scroll={{ x: 'max-content' }}
                    onRow={(record) => ({
                        className: 'cursor-pointer',
                        onClick(e) {
                            navigate(
                                {
                                    to: '/console/$orgId/tools/pds-compare/pds-documents/edit/$id',
                                    params: { orgId, id: String(record.id) },
                                },
                                e,
                            );
                        },
                    })}
                />
                <Outlet />
                <SmartPdsUploadModal
                    open={smartUploadOpen}
                    onClose={() => setSmartUploadOpen(false)}
                    onSuccess={() => {
                        setSmartUploadOpen(false);
                        trpcUtils.marketPds.listOrgPds.invalidate();
                        trpcUtils.marketPds.listCategories.invalidate();
                        trpcUtils.marketPds.listInsurers.invalidate();
                    }}
                    mode="user"
                />
            </TRPCErrorBoundary>
        </PageController>
    );
}
